import { OpenFeatureProvider } from '@openfeature/react-sdk';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { toDatadogEnv } from '@risksmart-app/shared/src/utils/datadog-environment';
import * as Sentry from '@sentry/react';
import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';

import { clearFeatureFlagContext, setFeatureFlagContext } from './openfeature';

interface Props {
  children: ReactNode;
}

/**
 * Mounts the OpenFeature React provider and keeps the global evaluation
 * context in sync with the signed-in user's organisation. Flag hooks
 * (`useBooleanFlagValue` etc.) re-render automatically on provider readiness,
 * context changes, and configuration changes (e.g. flagd hot reloads).
 * Provider registration itself happens once at boot in `index.tsx`.
 */
export const FeatureFlagProvider: FC<Props> = ({ children }) => {
  const { user } = useRisksmartUser();

  const orgKey = user?.orgKey;
  const userId = user?.userId;
  const tenantName = user?.tenantName;

  useEffect(() => {
    if (!orgKey) {
      // Logout / unauthenticated render — clear the stale identity. No
      // cleanup fn on the authed branch: it would transiently blank the
      // context on every identity change.
      clearFeatureFlagContext().catch((error) => {
        Sentry.captureException(error, {
          tags: { component: 'feature-flags' },
        });
      });

      return;
    }

    setFeatureFlagContext({
      orgId: orgKey,
      userId: userId || undefined,
      tenant: tenantName || undefined,
      // app→prod parity with the server's DD_ENV; `||` (not `??`) so an empty
      // REACT_APP_ENVIRONMENT also falls back.
      env: toDatadogEnv(getEnv('REACT_APP_ENVIRONMENT', true)) || 'unknown',
    }).catch((error) => {
      Sentry.captureException(error, { tags: { component: 'feature-flags' } });
    });
  }, [orgKey, userId, tenantName]);

  // Clear on unmount only — kept separate from the effect above so an
  // identity change never transiently blanks the context mid-session.
  useEffect(() => {
    return () => {
      void clearFeatureFlagContext();
    };
  }, []);

  return <OpenFeatureProvider>{children}</OpenFeatureProvider>;
};
